import React, { useMemo } from "react";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PropTypes from "prop-types";
import { Line } from "react-chartjs-2";
import Icon from "@mui/material/Icon";
import ChartDataLabels from "chartjs-plugin-datalabels";
import configs from "examples/Charts/LineCharts/ReportsLineChart/configs";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ChartDataLabels // register plugin
);

const splitTimestampParts = (value = "") => {
  if (!value) return [null, null];
  const normalized = String(value).trim();
  if (!normalized) return [null, null];

  if (normalized.includes("\n")) {
    const [first, ...rest] = normalized
      .split("\n")
      .map((part) => part.trim())
      .filter(Boolean);
    return [first || null, rest.join(" ") || null];
  }

  const isoMatch = normalized.match(/^(\d{4}-\d{2}-\d{2})(?:[T\s](.*))?$/);
  if (isoMatch) {
    return [isoMatch[1], isoMatch[2] ? isoMatch[2].replace(/Z$/, "").trim() || null : null];
  }

  const separators = [" | ", " - ", " – "];
  for (const separator of separators) {
    const index = normalized.indexOf(separator);
    if (index !== -1) {
      const first = normalized.slice(0, index).trim();
      const second = normalized.slice(index + separator.length).trim();
      return [first || normalized, second || null];
    }
  }

  const parts = normalized.split(/\s+/);
  if (parts.length > 1) {
    return [parts[0], parts.slice(1).join(" ") || null];
  }

  return [normalized, null];
};

function WaterQChart({
  color,
  title,
  chart,
  chartHeight,
  timestamp,
  waterq_temp_sensor,
  waterq_temp_reading,
  waterq_temp_status,
  waterq_temp_target = [],
  waterq_ph_sensor,
  waterq_ph_reading,
  waterq_ph_status,
  waterq_ph_target = [],
}) {
  const { data, options } = configs(chart.labels || [], chart.datasets || {});
  const [timestampDate, timestampTime] = splitTimestampParts(timestamp);

  return (
    <Card sx={{ height: "100%", pb: { xs: "1cm", sm: 0 } }}>
      <MDBox padding="1rem">
        {useMemo(
          () => (
            <MDBox
              variant="gradient"
              bgColor={color}
              borderRadius="lg"
              coloredShadow={color}
              py={2}
              pr={0.5}
              mt={-5}
              height={chartHeight}
            >
              <Line data={data} options={options} />
            </MDBox>
          ),
          [chart, color]
        )}
        <MDBox
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            position: "relative",
            pt: 3,
            pb: { xs: 4, sm: 1 },
            px: 1,
          }}
        >
          <MDBox
            sx={{
              display: "flex",
              flexDirection: { xs: "row", sm: "column" },
              alignItems: { xs: "flex-start", sm: "flex-start" },
              justifyContent: { xs: "space-between", sm: "flex-start" },
              gap: { xs: 1, sm: 0.75 },
              width: "100%",
            }}
          >
            <MDBox
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: { xs: 0.5, sm: 0.75 },
              }}
            >
              <MDTypography variant="h6" textTransform="capitalize">
                {title}
              </MDTypography>
              <MDTypography variant="h6" textTransform="capitalize">
                Device: {waterq_temp_sensor}
              </MDTypography>
            </MDBox>
            {timestamp ? (
              <>
                <MDBox
                  sx={{
                    display: { xs: "flex", sm: "none" },
                    flexDirection: "column",
                    alignItems: "flex-end",
                    fontSize: "0.95rem",
                    color: "text.secondary",
                    textAlign: "right",
                    ml: "auto",
                  }}
                >
                  <MDTypography variant="button" color="text">
                    {timestampDate || timestamp}
                  </MDTypography>
                  {timestampTime ? (
                    <MDTypography variant="button" color="text" fontWeight="light" sx={{ mt: 0.1 }}>
                      {timestampTime}
                    </MDTypography>
                  ) : null}
                </MDBox>
                <MDBox
                  sx={{
                    display: { xs: "none", sm: "flex" },
                    alignItems: "center",
                    gap: 0.75,
                    fontSize: "0.95rem",
                    color: "text.secondary",
                    mt: "0.5cm",
                  }}
                >
                  <Icon sx={{ fontSize: "1.2rem" }}>schedule</Icon>
                  <MDTypography
                    variant="button"
                    color="text"
                    fontWeight="light"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {timestamp}
                  </MDTypography>
                </MDBox>
              </>
            ) : null}
          </MDBox>

          <MDBox
            sx={{
              position: { xs: "static", sm: "absolute" },
              bottom: { sm: 55 },
              right: { sm: 16 },
              mt: { xs: 2, sm: 0 },
              display: "flex",
              flexDirection: { xs: "row", sm: "column" },
              alignItems: { xs: "center", sm: "flex-start" },
              gap: { xs: 1.5, sm: 0.5 },
              width: { xs: "100%", sm: "auto" },
              overflowX: { xs: "auto", sm: "visible" },
              pr: { xs: 1, sm: 0 },
              whiteSpace: { xs: "nowrap", sm: "normal" },
              transform: { xs: "translateY(-0.5cm)", sm: "none" },
            }}
          >
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ width: "100%" }}
            >
              <MDBox display="flex" alignItems="center">
                <MDTypography sx={{ fontSize: "1rem", minWidth: "55px" }} color="textSecondary">
                  Temp:
                </MDTypography>
                <MDBox
                  component="span"
                  sx={{
                    px: 1.2,
                    py: 0.2,
                    mx: 0.5,
                    borderRadius: "4px",
                    backgroundColor: (theme) => {
                      switch ((waterq_temp_status || "").toLowerCase()) {
                        case "green":
                          return theme.palette.success.main;
                        case "amber":
                          return theme.palette.warning.main;
                        case "red":
                          return theme.palette.error.main;
                        default:
                          return theme.palette.grey[400];
                      }
                    },
                    color: (theme) => theme.palette.common.white,
                    fontWeight: "bold",
                    fontSize: "1rem",
                  }}
                >
                  {waterq_temp_reading}°C
                </MDBox>
              </MDBox>

              {Array.isArray(waterq_temp_target) && waterq_temp_target.length === 2 && (
                <MDTypography sx={{ fontSize: "1rem" }} color="textSecondary">
                  ({waterq_temp_target[0]}-{waterq_temp_target[1]})
                </MDTypography>
              )}
            </MDBox>

            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ width: "100%" }}
            >
              <MDBox display="flex" alignItems="center">
                <MDTypography sx={{ fontSize: "1rem", minWidth: "55px" }} color="textSecondary">
                  pH:
                </MDTypography>
                <MDBox
                  component="span"
                  sx={{
                    px: 1.2,
                    py: 0.2,
                    mx: 0.5,
                    borderRadius: "4px",
                    backgroundColor: (theme) => {
                      switch ((waterq_ph_status || "").toLowerCase()) {
                        case "green":
                          return theme.palette.success.main;
                        case "amber":
                          return theme.palette.warning.main;
                        case "red":
                          return theme.palette.error.main;
                        default:
                          return theme.palette.grey[400];
                      }
                    },
                    color: (theme) => theme.palette.common.white,
                    fontWeight: "bold",
                    fontSize: "1rem",
                  }}
                >
                  {waterq_ph_reading}
                </MDBox>
              </MDBox>

              {Array.isArray(waterq_ph_target) && waterq_ph_target.length === 2 && (
                <MDTypography sx={{ fontSize: "1rem" }} color="textSecondary">
                  ({waterq_ph_target[0]}-{waterq_ph_target[1]})
                </MDTypography>
              )}
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

WaterQChart.defaultProps = {
  color: "info",
  description: "",
  canvasHeight: "12.5rem",
};

WaterQChart.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
  chartHeight: PropTypes.string,
  timestamp: PropTypes.string,
  waterq_temp_sensor: PropTypes.string,
  waterq_temp_reading: PropTypes.number,
  waterq_temp_status: PropTypes.string,
  waterq_temp_target: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.number]),
  waterq_ph_sensor: PropTypes.string,
  waterq_ph_reading: PropTypes.number,
  waterq_ph_status: PropTypes.string,
  waterq_ph_target: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.number), PropTypes.number]),
};

export default WaterQChart;
